import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { leadsAPI, contactAPI, supabase } from '../../lib/supabase';
import DealManagement from './DealManagement';
import CustomerManager from './CustomerManager';
import SafeIcon from '../../common/SafeIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Card, CardHeader, PageHeader, Button, EmptyState, useAdmChartColors } from './ui/AdminUI';
import '../../index.css';

import { FiUsers, FiMessageSquare, FiMail, FiTrendingUp, FiFileText, FiBarChart2, FiPieChart, FiDollarSign, FiUser, FiTruck, FiEdit, FiSettings, FiLogOut, FiX } from 'react-icons/fi';

const AdminDashboard = ({ onNavigate, setActiveTab: setParentTab }) => {
  // Admin.jsx passes setActiveTab; older callers passed onNavigate. Accept
  // either so every click lands somewhere instead of throwing.
  const openTab = (tab) => {
    const nav = onNavigate || setParentTab;
    if (nav) nav(tab);
  };
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    totalInquiries: 0,
    newInquiries: 0,
    totalLeads: 0,
    convertedLeads: 0,
    todayAppointments: 0,
    completedAppointments: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [chartData, setChartData] = useState({
    golfCarts: [],
    partSales: [],
    vehicleSales: [],
    leads: [],
    conversionRates: [],
    monthlyTrends: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load actual data from database
      const today = new Date().toISOString().split('T')[0];
      const [inquiries, leads, appointments] = await Promise.all([
        contactAPI.getInquiries(),
        leadsAPI.getLeads(),
        // Service appointments - skip if table doesn't exist
        supabase
          .from('service_appointments')
          .select('*')
          .eq('appointment_date', today)
          .then(result => result.data || [])
      ]);

      // Calculate stats
      const newInquiries = inquiries.filter(inquiry => inquiry.status === 'new').length;
      const convertedLeads = leads.filter(lead => lead.status === 'converted').length;
      const todayAppointments = appointments.data?.length || 0;
      const completedAppointments = appointments.data?.filter(apt => apt.status === 'completed').length || 0;

      setStats({
        totalInquiries: inquiries.length,
        newInquiries: newInquiries,
        totalLeads: leads.length,
        convertedLeads: convertedLeads,
        todayAppointments: todayAppointments,
        completedAppointments: completedAppointments
      });

      // Generate monthly chart data
      const monthlyData = generateMonthlyChartData(inquiries, leads);
      setChartData(monthlyData);

      // Create recent activity from actual data
      const allInquiries = inquiries.map(inquiry => ({
        id: `inquiry-${inquiry.id}`,
        type: 'inquiry',
        message: `New inquiry from ${inquiry.name}`,
        time: getTimeAgo(inquiry.created_at),
        timestamp: new Date(inquiry.created_at).getTime()
      }));

      const allLeads = leads.map(lead => ({
        id: `lead-${lead.id}`,
        type: 'lead',
        message: `New lead from ${lead.first_name} ${lead.last_name}`,
        time: getTimeAgo(lead.created_at),
        timestamp: new Date(lead.created_at).getTime()
      }));

      const allAppointments = appointments.data?.map(apt => ({
        id: `apt-${apt.id}`,
        type: 'appointment',
        message: `Service appointment for ${apt.customer_name}`,
        time: `${apt.appointment_time}`,
        timestamp: new Date(apt.created_at || apt.appointment_date).getTime()
      })) || [];

      // Merge all sources and sort by newest first
      const merged = [...allInquiries, ...allLeads, ...allAppointments]
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 20);

      setRecentActivity(merged);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
    }
  };

  const generateMonthlyChartData = (inquiries, leads) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = new Date().getFullYear();
    
    // Initialize monthly data structure
    const monthlyStats = months.map(month => ({
      month,
      golfCartInquiries: 0,
      golfCartSales: 0,
      partInquiries: 0,
      partSales: 0,
      vehicleInquiries: 0,
      vehicleSales: 0,
      totalLeads: 0,
      convertedLeads: 0
    }));

    // Process inquiries by category and month
    inquiries.forEach(inquiry => {
      const date = new Date(inquiry.created_at);
      if (date.getFullYear() === currentYear) {
        const monthIndex = date.getMonth();
        const category = inquiry.inquiry_type || inquiry.subject?.toLowerCase() || '';
        
        if (category.includes('golf') || category.includes('cart')) {
          monthlyStats[monthIndex].golfCartInquiries++;
          if (inquiry.status === 'converted') monthlyStats[monthIndex].golfCartSales++;
        } else if (category.includes('part') || category.includes('service')) {
          monthlyStats[monthIndex].partInquiries++;
          if (inquiry.status === 'converted') monthlyStats[monthIndex].partSales++;
        } else {
          monthlyStats[monthIndex].vehicleInquiries++;
          if (inquiry.status === 'converted') monthlyStats[monthIndex].vehicleSales++;
        }
      }
    });

    // Process leads by month
    leads.forEach(lead => {
      const date = new Date(lead.created_at);
      if (date.getFullYear() === currentYear) {
        const monthIndex = date.getMonth();
        monthlyStats[monthIndex].totalLeads++;
        if (lead.status === 'converted') monthlyStats[monthIndex].convertedLeads++;
      }
    });

    return {
      golfCarts: monthlyStats.map(m => ({
        month: m.month,
        inquiries: m.golfCartInquiries,
        sales: m.golfCartSales,
        conversionRate: m.golfCartInquiries > 0 ? ((m.golfCartSales / m.golfCartInquiries) * 100).toFixed(1) : 0
      })),
      partSales: monthlyStats.map(m => ({
        month: m.month,
        inquiries: m.partInquiries,
        sales: m.partSales,
        conversionRate: m.partInquiries > 0 ? ((m.partSales / m.partInquiries) * 100).toFixed(1) : 0
      })),
      vehicleSales: monthlyStats.map(m => ({
        month: m.month,
        inquiries: m.vehicleInquiries,
        sales: m.vehicleSales,
        conversionRate: m.vehicleInquiries > 0 ? ((m.vehicleSales / m.vehicleInquiries) * 100).toFixed(1) : 0
      })),
      leads: monthlyStats.map(m => ({
        month: m.month,
        total: m.totalLeads,
        converted: m.convertedLeads,
        conversionRate: m.totalLeads > 0 ? ((m.convertedLeads / m.totalLeads) * 100).toFixed(1) : 0
      })),
      conversionRates: monthlyStats.map(m => ({
        month: m.month,
        golfCarts: m.golfCartInquiries > 0 ? ((m.golfCartSales / m.golfCartInquiries) * 100).toFixed(1) : 0,
        parts: m.partInquiries > 0 ? ((m.partSales / m.partInquiries) * 100).toFixed(1) : 0,
        vehicles: m.vehicleInquiries > 0 ? ((m.vehicleSales / m.vehicleInquiries) * 100).toFixed(1) : 0,
        leads: m.totalLeads > 0 ? ((m.convertedLeads / m.totalLeads) * 100).toFixed(1) : 0
      })),
      monthlyTrends: monthlyStats.map(m => ({
        month: m.month,
        totalInquiries: m.golfCartInquiries + m.partInquiries + m.vehicleInquiries,
        totalSales: m.golfCartSales + m.partSales + m.vehicleSales,
        totalLeads: m.totalLeads
      }))
    };
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Less than an hour ago';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  const chart = useAdmChartColors();

  const statCards = [
    {
      title: 'Total Inquiries',
      value: stats.totalInquiries,
      icon: FiMessageSquare,
      description: 'Contact form submissions',
      target: 'leads'
    },
    {
      title: 'New Inquiries',
      value: stats.newInquiries,
      icon: FiMail,
      description: 'Unread inquiries',
      target: 'leads'
    },
    {
      title: 'Sales Leads',
      value: stats.totalLeads,
      icon: FiUsers,
      description: 'Qualified leads',
      target: 'leads'
    },
    {
      title: 'Converted',
      value: stats.convertedLeads,
      icon: FiTrendingUp,
      description: 'Successful sales',
      target: 'deals'
    }
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Dashboard"
        description="Welcome back! Here's what's happening at Paradise-Performance."
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, index) => (
          <div
            key={index}
            className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4 cursor-pointer
              hover:bg-adm-bg-2 transition-colors duration-150"
            onClick={() => openTab(card.target)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openTab(card.target); }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-medium text-adm-text-mid">{card.title}</p>
                <p className="mt-2 text-[26px] leading-none font-semibold text-adm-text-hi tabular-nums">
                  {card.value}
                </p>
              </div>
              <SafeIcon icon={card.icon} className="w-4 h-4 text-adm-text-lo shrink-0" />
            </div>
            <p className="text-xs text-adm-text-lo mt-3">{card.description}</p>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader
          title="Recent Activity"
          actions={
            <Button onClick={() => setShowAllActivity(!showAllActivity)}>
              {showAllActivity ? 'Show Less' : 'View All'}
            </Button>
          }
        />
        {recentActivity.length === 0 ? (
          <EmptyState
            icon={FiMessageSquare}
            title="No recent activity"
            description="New leads and inquiries will appear here as they come in."
          />
        ) : (
          <div>
            {(showAllActivity ? recentActivity : recentActivity.slice(0, 5)).map((activity) => (
              <div
                key={activity.id}
                className="flex items-start gap-3 px-5 py-3 border-b border-adm-border last:border-b-0
                  group hover:bg-adm-bg-2 transition-colors duration-150"
              >
                <span className={`w-1.5 h-1.5 rounded-full mt-[7px] shrink-0 ${
                  activity.type === 'lead' ? 'bg-adm-success' :
                  activity.type === 'inquiry' ? 'bg-adm-blue' :
                  'bg-adm-text-lo'
                }`} aria-hidden="true"></span>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-adm-text-hi">{activity.message}</p>
                  <p className="text-xs text-adm-text-lo mt-0.5 tabular-nums">{activity.time}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setRecentActivity(recentActivity.filter(a => a.id !== activity.id));
                  }}
                  className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-adm-text-lo
                    hover:text-adm-danger transition-opacity shrink-0"
                  aria-label="Dismiss activity"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Golf Cart Sales */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-adm-text-hi flex items-center gap-2">
              <SafeIcon icon={FiBarChart2} className="w-4 h-4 text-adm-text-lo" />
              <span>Golf Cart Sales</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData.golfCarts}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: chart.grid }} />
              <YAxis tickLine={false} axisLine={false} width={36} />
              <Tooltip cursor={{ fill: 'oklch(var(--adm-bg-3) / 0.6)' }} />
              <Legend />
              <Bar dataKey="inquiries" fill={chart.blue} name="Inquiries" radius={[3, 3, 0, 0]} />
              <Bar dataKey="sales" fill={chart.cmp} name="Sales" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Lead Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-adm-text-hi flex items-center gap-2">
              <SafeIcon icon={FiUsers} className="w-4 h-4 text-adm-text-lo" />
              <span>Lead Performance</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData.leads}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: chart.grid }} />
              <YAxis tickLine={false} axisLine={false} width={36} />
              <Tooltip cursor={{ stroke: chart.grid, strokeDasharray: '3 3' }} />
              <Legend />
              <Line type="monotone" dataKey="total" stroke={chart.blue} name="Total Leads" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
              <Line type="monotone" dataKey="converted" stroke={chart.cmp} name="Converted" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Part Sales Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-adm-text-hi flex items-center gap-2">
              <SafeIcon icon={FiFileText} className="w-4 h-4 text-adm-text-lo" />
              <span>Parts & Service</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData.partSales}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: chart.grid }} />
              <YAxis tickLine={false} axisLine={false} width={36} />
              <Tooltip cursor={{ fill: 'oklch(var(--adm-bg-3) / 0.6)' }} />
              <Legend />
              <Bar dataKey="inquiries" fill={chart.blue} name="Inquiries" radius={[3, 3, 0, 0]} />
              <Bar dataKey="sales" fill={chart.cmp} name="Sales" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Vehicle Sales Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-adm-text-hi flex items-center gap-2">
              <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-adm-text-lo" />
              <span>Vehicle Sales</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData.vehicleSales}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: chart.grid }} />
              <YAxis tickLine={false} axisLine={false} width={36} />
              <Tooltip cursor={{ stroke: chart.grid, strokeDasharray: '3 3' }} />
              <Legend />
              <Line type="monotone" dataKey="inquiries" stroke={chart.blue} name="Inquiries" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
              <Line type="monotone" dataKey="sales" stroke={chart.cmp} name="Sales" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Conversion Rates Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-adm-text-hi flex items-center gap-2">
              <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-adm-text-lo" />
              <span>Conversion Rates Trend</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData.conversionRates}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: chart.grid }} />
              <YAxis tickLine={false} axisLine={false} width={36} />
              <Tooltip cursor={{ stroke: chart.grid, strokeDasharray: '3 3' }} formatter={(value) => [`${value}%`, 'Conversion Rate']} />
              <Legend />
              <Line type="monotone" dataKey="golfCarts" stroke={chart.blue} name="Golf Carts" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
              <Line type="monotone" dataKey="parts" stroke={chart.cmp} name="Parts" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
              <Line type="monotone" dataKey="vehicles" stroke={chart.chart2} name="Vehicles" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
              <Line type="monotone" dataKey="leads" stroke={chart.chart4} name="Leads" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Monthly Trends Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-adm-text-hi flex items-center gap-2">
              <SafeIcon icon={FiPieChart} className="w-4 h-4 text-adm-text-lo" />
              <span>Monthly Business Overview</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData.monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
              <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: chart.grid }} />
              <YAxis tickLine={false} axisLine={false} width={36} />
              <Tooltip cursor={{ stroke: chart.grid, strokeDasharray: '3 3' }} />
              <Legend />
              <Line type="monotone" dataKey="totalInquiries" stroke={chart.blue} name="Total Inquiries" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
              <Line type="monotone" dataKey="totalSales" stroke={chart.cmp} name="Total Sales" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
              <Line type="monotone" dataKey="totalLeads" stroke={chart.chart2} name="Total Leads" strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Performance Tables */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.16, ease: 'easeOut' }}
        className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4"
      >
        <h2 className="text-[15px] font-semibold text-adm-text-hi mb-4">Performance Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-adm-bg-2 rounded-adm-card p-4">
            <div className="text-[26px] leading-none font-semibold text-adm-text-hi tabular-nums">
              {((stats.convertedLeads / stats.totalLeads) * 100 || 0).toFixed(1)}%
            </div>
            <p className="text-xs text-adm-text-mid mt-2">Lead Conversion Rate</p>
          </div>
          <div className="bg-adm-bg-2 rounded-adm-card p-4">
            <div className="text-[26px] leading-none font-semibold text-adm-text-hi tabular-nums">
              {stats.totalInquiries + stats.totalLeads}
            </div>
            <p className="text-xs text-adm-text-mid mt-2">Total Interactions</p>
          </div>
          <div className="bg-adm-bg-2 rounded-adm-card p-4">
            <div className="text-[26px] leading-none font-semibold text-adm-accent tabular-nums">
              {stats.newInquiries}
            </div>
            <p className="text-xs text-adm-text-mid mt-2">Pending Responses</p>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.16, ease: 'easeOut' }}
        className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4"
      >
        <h2 className="text-[15px] font-semibold text-adm-text-hi mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { label: 'View All Leads', icon: FiUsers, target: 'leads' },
            { label: 'Open Deal Builder', icon: FiFileText, target: 'deal-builder' },
            { label: 'Manage Inventory', icon: FiTruck, target: 'vehicles' },
          ].map((action) => (
            <button
              key={action.target}
              type="button"
              onClick={() => openTab(action.target)}
              className="flex items-center gap-3 p-3 rounded-adm-ctl bg-adm-bg-2 text-left
                shadow-[inset_0_0_0_1px_oklch(var(--adm-border))]
                hover:bg-adm-bg-3 transition-colors duration-150"
            >
              <SafeIcon icon={action.icon} className="w-4 h-4 text-adm-text-lo shrink-0" />
              <span className="text-[13px] font-medium text-adm-text-hi">{action.label}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
